/**
 * Past conversations.
 *
 * Grouped by age rather than listed with dates, because the question somebody
 * has when they open this is "where's the one from this morning?" and a column
 * of `2026-08-04` does not answer it as fast as a heading that says Yesterday.
 *
 * Deleting is deliberately quiet — no confirmation dialog. A chat thread is not
 * a ledger entry; nothing in it was applied unless it was applied through the
 * Apply button, which writes to the ledger and leaves its own audit trail.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import type { ConversationSummary } from './types';

function bucketOf(iso: string): 'today' | 'yesterday' | 'week' | 'older' {
  const then = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = then.getTime();
  if (t >= startOfToday) return 'today';
  if (t >= startOfToday - 86_400_000) return 'yesterday';
  if (t >= startOfToday - 7 * 86_400_000) return 'week';
  return 'older';
}

const ORDER = ['today', 'yesterday', 'week', 'older'] as const;

export function ConversationList({
  activeId,
  onPick,
  className,
}: {
  activeId: string | null;
  onPick: (id: string) => void;
  className?: string;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();

  const { data } = useQuery<{ conversations: ConversationSummary[] }, ApiError>({
    queryKey: ['agent-conversations'],
    queryFn: () => api<{ conversations: ConversationSummary[] }>('/agent/conversations'),
    staleTime: 30_000,
  });

  const conversations = data?.conversations ?? [];
  if (conversations.length === 0) {
    return (
      <p className={cn('px-2 py-6 text-center text-[12px] text-muted-foreground', className)}>
        {t('agent_no_history')}
      </p>
    );
  }

  const groups = new Map<string, ConversationSummary[]>();
  for (const c of conversations) {
    const key = bucketOf(c.updated_at);
    groups.set(key, [...(groups.get(key) ?? []), c]);
  }

  const remove = async (id: string) => {
    await api(`/agent/conversations/${id}`, { method: 'DELETE' }).catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ['agent-conversations'] });
  };

  return (
    <div className={cn('space-y-3', className)}>
      {ORDER.filter((k) => groups.has(k)).map((key) => (
        <div key={key}>
          <p className="px-2 pb-1 text-[10.5px] font-medium tracking-wide text-muted-foreground uppercase">
            {t(`agent_when_${key}`)}
          </p>
          <ul>
            {groups.get(key)!.map((c) => (
              <li key={c.id} className="group/row relative">
                <button
                  type="button"
                  dir="auto"
                  onClick={() => onPick(c.id)}
                  className={cn(
                    'w-full truncate rounded-md px-2 py-1.5 pe-8 text-start text-[12.5px]',
                    'hover:bg-accent',
                    c.id === activeId && 'bg-accent font-medium',
                  )}
                >
                  {c.title || t('agent_untitled')}
                </button>
                <button
                  type="button"
                  aria-label={t('delete')}
                  onClick={() => void remove(c.id)}
                  className={cn(
                    'absolute end-1 top-1/2 -translate-y-1/2 rounded p-1',
                    'text-muted-foreground opacity-0 hover:text-foreground',
                    'group-hover/row:opacity-100 focus-visible:opacity-100',
                  )}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
